import { token } from '@atlaskit/tokens';
import React, {
  type CSSProperties,
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import { type ImperativePanelHandle, getPanelGroupElement } from 'react-resizable-panels';
import styled from 'styled-components';

import { messages } from './i18n';

interface AccessibleResizeHandleProps {
  id: string;
  disabled?: boolean;
  style?: CSSProperties;
  onDragging?: (isDragging: boolean) => void;
  panelRef: React.RefObject<ImperativePanelHandle | null>;
  minSize: number;
  maxSize: number;
}

const KEYBOARD_STEP = 1;
const PANEL_GROUP_ID = 'panel-group';
const HIT_AREA_MARGIN = token('space.negative.050', '-4px');

const ResizeHandle = styled.div`
  flex: 0 0 0;
  position: relative;
  outline: none;
  touch-action: none;
  user-select: none;
  cursor: col-resize;

  &::before {
    content: '';
    position: absolute;
    top: ${token('space.0', '0')};
    bottom: ${token('space.0', '0')};
    left: ${HIT_AREA_MARGIN};
    right: ${HIT_AREA_MARGIN};
  }

  &[data-disabled='true'] {
    cursor: default;
    pointer-events: none;
  }
`;

const AccessibleResizeHandle: FunctionComponent<AccessibleResizeHandleProps> = ({
  id,
  disabled = false,
  style,
  onDragging,
  panelRef,
  minSize,
  maxSize,
}) => {
  const { formatMessage } = useIntl();
  const handleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentSize, setCurrentSize] = useState<number | null>(null);
  const dragStartRef = useRef<{ startX: number; startSize: number } | null>(null);

  useEffect(() => {
    if (panelRef.current) {
      setCurrentSize(panelRef.current.getSize());
    }
  }, [panelRef]);

  const updateSize = useCallback(
    (newSize: number) => {
      if (!panelRef.current || disabled) {
        return;
      }

      const clampedSize = Math.max(minSize, Math.min(maxSize, newSize));
      panelRef.current.resize(clampedSize);
      setCurrentSize(clampedSize);
    },
    [panelRef, disabled, minSize, maxSize]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled || !panelRef.current) {
        return;
      }

      const current = panelRef.current.getSize();
      let newSize = current;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          newSize = current + KEYBOARD_STEP;
          event.preventDefault();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          newSize = current - KEYBOARD_STEP;
          event.preventDefault();
          break;
        case 'Home':
          newSize = maxSize;
          event.preventDefault();
          break;
        case 'End':
          newSize = minSize;
          event.preventDefault();
          break;
        default:
          return;
      }

      updateSize(newSize);
    },
    [disabled, panelRef, updateSize, minSize, maxSize]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (disabled || !panelRef.current) {
        return;
      }

      event.preventDefault();
      const target = event.currentTarget as HTMLElement;
      target.setPointerCapture(event.pointerId);

      dragStartRef.current = {
        startX: event.clientX,
        startSize: panelRef.current.getSize(),
      };

      setIsDragging(true);
      onDragging?.(true);
    },
    [disabled, panelRef, onDragging]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging || !dragStartRef.current || disabled) {
        return;
      }

      const groupElement = getPanelGroupElement(PANEL_GROUP_ID);
      if (!groupElement) {
        return;
      }

      const groupRect = groupElement.getBoundingClientRect();
      const delta = dragStartRef.current.startX - event.clientX;
      const deltaPercent = (delta / groupRect.width) * 100;
      const newSize = dragStartRef.current.startSize + deltaPercent;

      updateSize(newSize);
    },
    [isDragging, disabled, updateSize]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging) {
        return;
      }

      const target = event.currentTarget as HTMLElement;
      target.releasePointerCapture(event.pointerId);

      dragStartRef.current = null;
      setIsDragging(false);
      onDragging?.(false);
    },
    [isDragging, onDragging]
  );

  const handleMouseEnter = useCallback(() => {
    if (!disabled) {
      setIsHovered(true);
    }
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const ariaValueNow = currentSize !== null ? Math.round(currentSize) : undefined;

  const getResizeHandleState = () => {
    if (isDragging) {
      return 'drag';
    }
    if (isHovered || isFocused) {
      return 'hover';
    }
    return 'inactive';
  };

  return (
    <ResizeHandle
      ref={handleRef}
      id={id}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={formatMessage(messages['layout.resize-handle.aria-label'])}
      aria-valuemin={Math.round(minSize)}
      aria-valuemax={Math.round(maxSize)}
      aria-valuenow={ariaValueNow}
      aria-orientation="horizontal"
      aria-disabled={disabled}
      data-dragging={isDragging}
      data-disabled={disabled}
      data-resize-handle-state={getResizeHandleState()}
      style={style}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default AccessibleResizeHandle;
